"use client";

import AddCircleOutlineIcon from "@mui/icons-material/AddCircleOutline";
import { Card, CircularProgress, FormControl, InputLabel, MenuItem, Select } from "@mui/material";
import { Formik } from "formik";
import React, { useState } from "react";
import { useNavigate } from "react-router-dom";

import MDBox from "@/components/atoms/MDBox";
import { MDButton } from "@/components/atoms/MDButton";
import MDTypography from "@/components/atoms/MDTypography";
import PageContainer from "@/components/atoms/PageContainer";
import AlertBlock from "@/components/molecules/AlertBlock";
import Suspense from "@/components/molecules/Suspense";
import PageLayout from "@/components/templates/PageLayout";
import { PRODUCT_PERMISSIONS } from "@/constants/modules.permissions";
import ROUTE_URLS from "@/constants/route-urls";
import useLanguage from "@/contexts/language-context";
import useGetProducts from "@/hooks/requests/product/use-get-products";
import useHasNoPermission from "@/hooks/use-check-permissions";
import useFilters from "@/hooks/use-filters";
import usePagination from "@/hooks/use-pagination";
import IProduct from "@/interfaces/product.interface";
import { ProductsMessages } from "@/messages/products/product.messages";

import ProductFiltersForm from "./components/ProductFiltersForm";
import { ProductsImportModal } from "./components/ProductsImportModal";
import { ProductTable } from "./components/ProductTable/index";
import { initialFilterValues } from "./constants";
import { ProductSortField, SortDirection } from "./types";

type Props = {
  messages: ProductsMessages;
};

const FIRST_PAGE = 0;
const CARD_MARGIN_TOP = 16;
const LOADING_SPINNER_SIZE = 50;

export default function ProductsPage({ messages }: Props) {
  const router = useNavigate();
  const { lang } = useLanguage();
  const pagination = usePagination();
  const { pageNumber, pageSize, changePage } = pagination;
  const { filters, changeFilters } = useFilters<IProduct>({
    initialValue: initialFilterValues,
  });
  const hasNoPermissionToCreate = useHasNoPermission(PRODUCT_PERMISSIONS.CREATE);
  const [openImportProductsModal, setOpenImportProductsModal] = useState(false);
  const [hasAppliedFilter, setHasAppliedFilter] = useState(false);
  const [sortField, setSortField] = useState<ProductSortField>("code");
  const [sortDirection, setSortDirection] = useState<SortDirection>("asc");

  const handleOpenImportProductsModal = () => {
    setOpenImportProductsModal(true);
  };

  const handleCloseImportProductsModal = () => {
    setOpenImportProductsModal(false);
  };

  const { data, isLoading, error } = useGetProducts({
    filters,
    pageNumber,
    pageSize,
    sortField,
    sortDirection,
    enabled: hasAppliedFilter,
  });

  const shouldLoadProductsTable =
    hasAppliedFilter && !!data && !isLoading && !error && data.items.length > 0;
  const shouldLoadError = hasAppliedFilter && !data && !isLoading && !!error;
  const isString = typeof data === "string";
  const noFound =
    hasAppliedFilter && ((!!data && !isLoading && !error && data.items.length === 0) || isString);
  const shouldShowFilterPrompt = !hasAppliedFilter;

  return (
    <PageLayout messages={messages} title={messages.productTableTitle}>
      <PageContainer>
        <Formik
          initialValues={filters}
          validateOnChange={false}
          onSubmit={(submittedFilters) => {
            changePage(FIRST_PAGE);
            changeFilters(submittedFilters);
            setHasAppliedFilter(true);
          }}
        >
          <ProductFiltersForm messages={messages} />
        </Formik>

        <Card sx={({ functions: { pxToRem } }) => ({ marginTop: pxToRem(CARD_MARGIN_TOP) })}>
          <Suspense
            isSuspended={isLoading}
            fallback={<CircularProgress size={LOADING_SPINNER_SIZE} />}
          >
            <MDBox p={2}>
              <MDBox p={2} display="flex" flexWrap="wrap" gap={2} alignItems="center">
                <MDTypography variant="h6" sx={{ flex: "1 1 auto" }}>
                  {messages.productTableTitle}
                </MDTypography>

                <MDBox display="flex" gap={2} alignItems="center" flexWrap="wrap">
                  <FormControl size="small" sx={{ minWidth: 160 }}>
                    <InputLabel shrink>{messages.sortByLabel}</InputLabel>
                    <Select
                      value={sortField}
                      label={messages.sortByLabel}
                      onChange={(e) => {
                        setSortField(e.target.value as ProductSortField);
                        changePage(FIRST_PAGE);
                      }}
                      slotProps={{ input: { "aria-label": messages.sortByLabel } }}
                    >
                      <MenuItem value="code">{messages.codeOption}</MenuItem>
                      <MenuItem value="title">{messages.titleOption}</MenuItem>
                      <MenuItem value="brand">{messages.brandOption}</MenuItem>
                      <MenuItem value="active">{messages.statusOption}</MenuItem>
                    </Select>
                  </FormControl>
                  <FormControl size="small" sx={{ minWidth: 140 }}>
                    <InputLabel shrink>{messages.sortDirectionLabel}</InputLabel>
                    <Select
                      value={sortDirection}
                      label={messages.sortDirectionLabel}
                      onChange={(e) => {
                        setSortDirection(e.target.value as SortDirection);
                        changePage(FIRST_PAGE);
                      }}
                      slotProps={{ input: { "aria-label": messages.sortDirectionLabel } }}
                    >
                      <MenuItem value="asc">{messages.sortDirectionAsc}</MenuItem>
                      <MenuItem value="desc">{messages.sortDirectionDesc}</MenuItem>
                    </Select>
                  </FormControl>
                </MDBox>

                <MDBox display="flex" gap={2}>
                  <MDButton variant="gradient" color="dark" onClick={handleOpenImportProductsModal}>
                    {messages.importProductsButton}
                  </MDButton>

                  <MDButton
                    variant="gradient"
                    color="dark"
                    onClick={() => router(ROUTE_URLS.createProduct(lang))}
                    disabled={hasNoPermissionToCreate}
                    isLoading={isLoading}
                    endIcon={<AddCircleOutlineIcon />}
                  >
                    {messages.addProductButton}
                  </MDButton>
                </MDBox>
              </MDBox>

              {!!shouldLoadProductsTable && (
                <ProductTable pagination={pagination} data={data} messages={messages} />
              )}

              <ProductsImportModal
                messages={messages}
                open={openImportProductsModal}
                onClose={handleCloseImportProductsModal}
              />

              {!!shouldLoadError && (
                <AlertBlock
                  title={messages.errorToLoad}
                  description={error.message}
                  alt={messages.notFoundAlt}
                />
              )}

              {!!shouldShowFilterPrompt && (
                <AlertBlock
                  title={messages.filterPromptTitle}
                  description={messages.filterPromptDescription}
                  alt={messages.filterPromptAlt}
                  textPosition="above"
                />
              )}

              {!!noFound && (
                <AlertBlock
                  title={messages.notFoundError}
                  description={messages.notFoundDescription}
                  alt={messages.notFoundAlt}
                />
              )}
            </MDBox>
          </Suspense>
        </Card>
      </PageContainer>
    </PageLayout>
  );
}
